import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';

import { notUndefined } from 'ui/base/types';

import { Optional } from 'services/base/types';
import { getResponseBytes, http } from 'services/base/http';

import {
  IPangolinDomain,
  UpdateRelease,
  UpdateReleaseMap,
} from './types';

import {
  UpdateReleaseObject as UpdateReleaseProto,

  GetUpdateReleaseResponse,
  GetUpdateReleasesResponse,
} from './protos/update_pb';

export class UpdateReleaseService {
  static async fetch(domain: IPangolinDomain, id: string, save = true): Promise<Optional<UpdateRelease>> {
    const httpResp = await http.get(`/pangolin/${domain.id}/update/releases/${id}`);
    return runInAction(() => {
      const remoteRelease = parseUpdateReleaseResponse(httpResp);
      if (remoteRelease == null) return undefined;

      if (save) {
        const localRelease = domain.updateReleases.get(id);
        if (localRelease == null) {
          domain.updateReleases.set(remoteRelease.id, remoteRelease);
          domain.updateReleases.sort(releaseSorter);
        } else {
          Object.assign(localRelease, remoteRelease);
        }
      }

      return remoteRelease;
    });
  }

  static async fetchAll(domain: IPangolinDomain, save = true): Promise<UpdateReleaseMap> {
    const httpResp = await http.get(`/pangolin/${domain.id}/update/releases`);
    const remoteReleases = parseUpdateReleasesResponse(httpResp);
    return runInAction(() => {
      if (save) domain.updateReleases = remoteReleases;
      return remoteReleases;
    });
  }
}

function parseUpdateReleaseResponse(httpResp: AxiosResponse): Optional<UpdateRelease> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetUpdateReleaseResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getRelease();
  return releaseFromProto(itemProto);
}

function parseUpdateReleasesResponse(httpResp: AxiosResponse): UpdateReleaseMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetUpdateReleasesResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getReleasesList();
  const releaseObjects = listProto.map(releaseFromProto).filter(notUndefined);
  return new UpdateReleaseMap(releaseObjects.map((r) => [r.id, r]));
}

function releaseFromProto(proto: Optional<UpdateReleaseProto>): Optional<UpdateRelease> {
  if (proto == null) return undefined;

  const id = proto.getId();
  const tag = proto.getTag();
  const notes = proto.getNotes();
  const is_enabled = proto.getIsEnabled();
  const created_at = proto.getCreatedAt();
  const updated_at = proto.getUpdatedAt();

  if (id === undefined) return undefined;
  if (tag === undefined) return undefined;
  if (is_enabled === undefined) return undefined;
  if (created_at === undefined) return undefined;
  if (updated_at === undefined) return undefined;

  return {
    id, tag, notes, is_enabled,
    created_at: created_at.toDate(),
    updated_at: updated_at.toDate(),
  };
}

function releaseSorter(a: UpdateRelease, b: UpdateRelease): number {
  const aCreatedAt = a.created_at.getTime();
  const bCreatedAt = b.created_at.getTime();
  return aCreatedAt - bCreatedAt;
}
